/**
 * `<system_time>` tag — invisibly appended to every user message at
 * send time. The assistant sees it (because it's part of the message
 * text), the chat UI strips it before rendering (so the user never
 * sees it), and it persists across sessions (because it's just part
 * of the stored message text — no separate field).
 *
 * Why: gives the model a definitive timestamp for every user turn,
 * useful for time-relative answers ("just now", "a few hours ago"),
 * agent self-memory entries ("user asked X on date Y"), and any
 * temporal reasoning that beats inferring from message order alone.
 *
 * Format: ISO 8601, e.g. `<system_time>2026-05-19T01:23:45.678Z</system_time>`
 */

const TAG_REGEX = /\n*<system_time>[^<]*<\/system_time>\s*$/g;

/**
 * Append a timestamp tag to a user message text. Idempotent — if the
 * text already ends with a system_time tag, replace it rather than
 * stacking another one (edge case: client re-submits without clearing).
 */
export function appendSystemTime(text: string): string {
  const trimmed = text.replace(TAG_REGEX, '').replace(/\s+$/, '');
  const iso = new Date().toISOString();
  // Single newline before the tag keeps it tight against the user's
  // text but on its own line — the model parses easily, and a
  // markdown renderer wouldn't merge it into the previous paragraph
  // if the strip pass somehow misses (defensive).
  return `${trimmed}\n<system_time>${iso}</system_time>`;
}

/**
 * Remove every `<system_time>...</system_time>` tag (anywhere in the
 * string, not just trailing) for display. Defensive: covers cases
 * where a tag ends up mid-text via copy-paste or future-format
 * variations.
 */
export function stripSystemTime(text: string): string {
  return text.replace(/\n*<system_time>[^<]*<\/system_time>\s*/g, '').trim();
}
